import InstagramIcon from '@mui/icons-material/Instagram';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import {
  SelectInput, TextInput, useDataProvider, useGetList, useNotify, useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Else, If, Then } from 'react-if';

import type { Contact, IndustryContributor, Resource } from '@/Api/studioServerTypes';
import MobileTelephoneInput from '@/Components/MobileTelephoneInput';
import { getResourceFilter } from '@/constants';
import { buildContactPutPayload } from '@/Helper/Payload/buildContactPutPayload';
import { buildIndustryContributorPayload } from '@/Helper/Payload/buildIndustryContributorPayload';
import {
  validateInstagramUrl,
} from '@/Helper/Validation/Validator';

export default function IndustryContactSection(): JSX.Element {
  const record = useRecordContext<IndustryContributor>();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { getValues, setValue, control } = useFormContext();

  const industryContributorNameParts = (record?.Name ?? '').trim().split(/\s+/).filter(Boolean);
  const defaultFirstName = industryContributorNameParts[0] ?? '';
  const defaultLastName = industryContributorNameParts.slice(1).join(' ');
  const [isSaving, setIsSaving] = useState(false);

  const warn = (message: string): void => notify(message, { type: 'warning' });

  const { data: engineers = [], isLoading: loadingEngineers } = useGetList<Resource>('resources', {
    sort: { field: 'Name', order: 'ASC' },
    filter: { ...getResourceFilter() },
  });

  const instagramUrl = useWatch({
    control,
    name: 'ContactIdentifierNavigation.InstagramUrl',
  }) as string | undefined;

  const isInstagramButtonEnabled = useMemo(() => {
    if (!instagramUrl) return false;
    return validateInstagramUrl(instagramUrl.trim()) === true;
  }, [instagramUrl]);

  const openInstagram = (): void => {
    if (!isInstagramButtonEnabled) return;
    window.open(instagramUrl?.trim(), '_blank');
  };

  const saveContact = async (): Promise<void> => {
    //     if (!getEditedContact.FirstName || !getEditedContact.LastName || !getEditedContact.Email || !getEditedContact.MobileTelephone) {
    //       warn('All contact fields are required.');
    //       return;
    //     }
    //     if (!getEditedIndustryContributor.AssignedResourceIdentifier) {
    //       warn('Please select an engineer.');
    //       return;
    //     }
    //
    //     const emailResult = validateEmail(getEditedContact.Email);
    //     if (emailResult !== true) {
    //       warn(emailResult as string);
    //       return;
    //     }
    //
    //     const phoneResult = validatePhoneNumber(getEditedContact.MobileTelephone);
    //     if (phoneResult !== true) {
    //       warn(phoneResult as string);
    //       return;
    //     }
    //
    //     const instagramResult = validateInstagramUrl(getEditedContact.InstagramUrl ?? '');
    //     if (instagramResult !== true) {
    //       warn(instagramResult as string);
    //       return;
    //     }

    setIsSaving(true);
    try {
      let recordContact = record?.ContactIdentifierNavigation ?? null;
      let contactId = recordContact?.ContactIdentifier ?? null as string | null;

      if (!contactId) {
        const createRes = await dataProvider.create('contacts', {
          data: {
            AccountNumber: 1,
            ContactIdentifier: crypto.randomUUID(),
            AddedBy: 'SystemAutoGenerated',
            ModifiedBy: 'SystemAutoGenerated',
            DateAdded: new Date().toISOString(),
            DateModified: new Date().toISOString(),
          },
        });

        const createdNewContact = createRes.data as Contact;
        contactId = createdNewContact.ContactIdentifier as string;

        setValue('ContactIdentifier', contactId, { shouldDirty: true });

        recordContact = createdNewContact;
      }

      const editedContact = getValues('ContactIdentifierNavigation') as Partial<Contact>;
      const editedIndustryContributor = getValues() as Partial<IndustryContributor>;

      const contactPayload = buildContactPutPayload(recordContact!, editedContact);
      const industryContributorPayload = buildIndustryContributorPayload(record!, editedIndustryContributor);
      if (editedIndustryContributor.AssignedResourceIdentifier === null) {
        industryContributorPayload.AssignedResourceIdentifier = null;
      }

      await dataProvider.update('contacts', {
        id: contactId!,
        data: contactPayload,
        previousData: recordContact!,
      });

      await dataProvider.update('industry-contributors', {
        id: record!.IndustryContributorIdentifier,
        data: industryContributorPayload,
        previousData: record,
      });

      notify('Contact saved', { type: 'success' });
      window.location.reload();
    } catch {
      warn('Failed to save contact');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      mb={3}
      width={{ xs: '100%', md: '90%' }}
      p={2}
      border={1}
      borderRadius={2}
      borderColor='grey.400'
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        void saveContact();
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1,
      }}
      >
        <Typography
          variant='h6' display='flex'
          alignItems='center' gap={1}
        >
          <PersonIcon />
          Contact
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip
            title={isInstagramButtonEnabled
              ? 'Open Instagram'
              : 'Please enter a valid Instagram URL'} arrow
          >
            <span>
              <Button
                onClick={openInstagram}
                variant='outlined'
                size='small'
                startIcon={<InstagramIcon />}
                sx={{ height: 36, minWidth: 140 }}
                disabled={isSaving || !isInstagramButtonEnabled}
              >
                Instagram
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title='Save Information' arrow
          >
            <Button
              onClick={saveContact}
              variant='contained'
              size='small'
              sx={{ height: 34, minWidth: 140 }}
              startIcon={(
                <If condition={isSaving}>
                  <Then>
                    <CircularProgress size={18} color='inherit' />
                  </Then>
                  <Else>
                    <SaveIcon />
                  </Else>
                </If>
              )}
              disabled={isSaving}
            >
              <If condition={isSaving}>
                <Then>Saving...</Then>
                <Else>Save</Else>
              </If>
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextInput
          source='ContactIdentifierNavigation.FirstName' label='First Name'
          fullWidth
          defaultValue={defaultFirstName}
        />
        <TextInput
          source='ContactIdentifierNavigation.LastName' label='Last Name'
          fullWidth
          defaultValue={defaultLastName}
        />
        <TextInput
          source='ContactIdentifierNavigation.Email' label='Email'
          type='email' fullWidth
        />
        <TextInput
          source='ContactIdentifierNavigation.City' label='City'
          fullWidth
        />
        <MobileTelephoneInput
          source='ContactIdentifierNavigation.MobileTelephone'
          label='Mobile Telephone'
          defaultCountry='us'

        />

        <TextInput
          source='ContactIdentifierNavigation.InstagramUrl' label='Instagram Link'
          type='url' fullWidth
        />
        <SelectInput
          source='AssignedResourceIdentifier'
          label='Assigned Engineer'
          fullWidth
          choices={engineers}
          optionValue='ResourceIdentifier'
          optionText='Name'
          disabled={loadingEngineers}
          emptyText='Unassigned'
          parse={(value: string | null | undefined) => value || null}
          resettable
        />

      </Box>
    </Box>
  );
}
